import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import usePermissions from "../../../../Hooks/usePermissions";
import { Modal } from "react-bootstrap";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  useMap,
} from "react-leaflet";
import { Box } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import DescriptionIcon from "@mui/icons-material/Description";
import CircularProgress from "@mui/material/CircularProgress";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import "./Forecast.css";
import RainfallForecast from "../../../common/RainfallForecast/RainfallForecast.js";
import LandslideAdvisory from "../Forecast/Landslide_Advisory.js";
import ImpactedAreaLS from "../Forecast/ImpactedArea_LS.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowCircleLeft,
  faArrowCircleRight,
} from "@fortawesome/free-solid-svg-icons";
import { translations, characteristicColors } from "./Translations.js";
import {
  generateLandslideAdvisory,
  getLandslideCharacteristic,
} from "./reportGenerator_LS.js";
import { getForecastDate } from "./dateUtils.js";
import Information from "./Information";
import { MapHomeControl, MapLegendControl } from "../../../common/MapControls/MapControls";


const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const INITIAL_VIEW = {
  lat: -8.8742,
  lng: 125.7275,
  zoom: 8,
};

// const getCharacteristic = (rainfall) => {
//   const rain = parseFloat(rainfall);
//   if (isNaN(rain)) return "N/A";
//   if (rain > -0.1 && rain <= 20) return "NoRisk";
//   if (rain > 20 && rain <= 50) return "Low";
//   if (rain > 50 && rain <= 100) return "Medium";
//   if (rain > 100) return "High";
//   return "N/A";
// };

const formatDateForDisplay = (dateString, offset) => {
  if (!dateString) return "--";
  return `${dateString}_UTC00 + ${offset}d : ${dateString}[UTC00-24]`;
};

const fetchAndProcessFullLandslideData = async () => {
  try {
    const [statsResponse, munisResponse, subMunisResponse, sucosResponse] = await Promise.all([
      axios.get(`${backendUrl}landslide/landslide-impact-risk-stats/current/`),
      axios.post(`${backendUrl}Municipalities/municipalities_list/`),
      axios.post(`${backendUrl}Municipalities/sub_municipalities_list/`),
      axios.post(`${backendUrl}Municipalities/suco_list/`),
    ]);

    const statsData = statsResponse.data;
    const munisData = munisResponse.data;
    const subMunisData = subMunisResponse.data;
    const sucosData = sucosResponse.data;
    const sucoGeoResponse = await fetch("/DATA/OSMbased_adm3_boundary.json");
    const sucoGeo = await sucoGeoResponse.json();

    const muniMap = new Map(munisData.map((m) => [m.adm1_code, m.adm1]));
    const subMuniMap = new Map(subMunisData.map((s) => [s.adm2_code, s.adm2]));
    const socioEconomicMap = new Map(
      sucoGeo.features.map((feature) => [
        feature.properties.ADM_Code,
        {
          Population: feature.properties.Population || 0,
          Male: feature.properties.Male || 0,
          Female: feature.properties.Female || 0,
          HH_Urban: feature.properties.HH_Urban || 0,
          HH_Rural: feature.properties.HH_Rural || 0,
          Household: feature.properties.Household || 0,
        },
      ])
    );

    const fullProcessedData = {};
    const tableDataByAdmCode = {};
    const forecastDates = [getForecastDate(0), getForecastDate(1), getForecastDate(2)];

    const statsLookup = statsData.reduce((acc, item) => {
      if (!acc[item.adm_code]) acc[item.adm_code] = {};
      acc[item.adm_code][item.date] = item;
      return acc;
    }, {});

    sucosData.forEach((sucoGeoApi) => {
      const adm_code = sucoGeoApi.adm3_code;
      const sucoName = sucoGeoApi.adm3;
      const adm1Code = adm_code.substring(0, 4);
      const adm2Code = adm_code.substring(0, 6);

      const socioData = socioEconomicMap.get(adm_code) || {
        Population: 0,
        Male: 0,
        Female: 0,
        HH_Urban: 0,
        HH_Rural: 0,
        Household: 0,
      };

      tableDataByAdmCode[adm_code] = {
        Municipality: muniMap.get(adm1Code) || "N/A",
        PostAdmin: subMuniMap.get(adm2Code) || "N/A",
        Suco: sucoName,
        admCode: adm_code,
        CharacteristicDay1: "N/A",
        CharacteristicDay2: "N/A",
        CharacteristicDay3: "N/A",
        Population: socioData.Population,
        Male: socioData.Male,
        Female: socioData.Female,
        HH_Urban: socioData.HH_Urban,
        HH_Rural: socioData.HH_Rural,
        Household: socioData.Household,
      };

      fullProcessedData[adm_code] = {
        info: {
          adm_code,
          suco: sucoName,
          municipality: muniMap.get(adm1Code) || "N/A",
          sub_municipality: subMuniMap.get(adm2Code) || "N/A",
          Population: socioData.Population,
          Male: socioData.Male,
          Female: socioData.Female,
          HH_Urban: socioData.HH_Urban,
          HH_Rural: socioData.HH_Rural,
          Household: socioData.Household,
        },
      };

      forecastDates.forEach((date, index) => {
        const dayKey = `CharacteristicDay${index + 1}`;
        const stat = statsLookup[adm_code]?.[date];
        if (stat) {
          fullProcessedData[adm_code][date] = {
            ...stat,
            municipality: tableDataByAdmCode[adm_code].Municipality,
            sub_municipality: tableDataByAdmCode[adm_code].PostAdmin,
            suco: sucoName,
            Population: socioData.Population,
            Male: socioData.Male,
            Female: socioData.Female,
            HH_Urban: socioData.HH_Urban,
            HH_Rural: socioData.HH_Rural,
            Household: socioData.Household,
          };
          tableDataByAdmCode[adm_code][dayKey] = stat.advisory;
        }
      });
    });

    Object.values(tableDataByAdmCode).forEach((item) => {
      if (!item.CharacteristicDay1) item.CharacteristicDay1 = "N/A";
      if (!item.CharacteristicDay2) item.CharacteristicDay2 = "N/A";
      if (!item.CharacteristicDay3) item.CharacteristicDay3 = "N/A";
    });

    const finalTableData = Object.values(tableDataByAdmCode).map((item, index) => ({ ...item, SlNo: index + 1 }));
    return { tableData: finalTableData, fullData: fullProcessedData, error: null };
  } catch (error) {
    console.error("Failed to fetch landslide data:", error);
    return { tableData: [], fullData: {}, error: error.message };
  }
};

// const fetchAllForecastMapData = async (type) => {
//   let baseUrl;
//   switch (type) {
//     case "municipality":
//       baseUrl = `${backendUrl}ecmwf/forecast/10_days_rainfall_municipality/`;
//       break;
//     case "sub_municipality":
//       baseUrl = `${backendUrl}ecmwf/forecast/10_days_rainfall_sub_municipality/`;
//       break;
//     case "suco":
//       baseUrl = `${backendUrl}ecmwf/forecast/10_days_rainfall_suco/`;
//       break;
//     default:
//       throw new Error("Invalid type for fetching forecast data");
//   }

//   const endpoint = baseUrl;

//   try {
//     const res = await fetch(endpoint, {
//       method: "GET",
//       headers: {
//         "Content-Type": "application/json",
//       },
//     });

//     if (!res.ok) {
//       const errorText = await res.text();
//       throw new Error(
//         `HTTP error! Status: ${res.status}, Message: ${errorText}`
//       );
//     }

//     const data = await res.json();
//     return data || [];
//   } catch (error) {
//     console.error(`Error fetching all ${type} forecast data:`, error);
//     return [];
//   }
// };


const fetchAllForecastMapData = async (type) => {
  let adminLevel;
  switch (type) {
    case "municipality":
      adminLevel = 1;
      break;
    case "sub_municipality":
      adminLevel = 2;
      break;
    case "suco":
      adminLevel = 3;
      break;
    default:
      throw new Error("Invalid type for fetching forecast data");
  }
 
  // Same endpoint the dashboard forecast map reads for its Suco / Post
  // Administrative / Municipality tabs (active forecast source, rainfall = "tp"),
  // so rainfall here always matches that map. Feeds the table, the Rainfall tab
  // and the advisory PDF.
  const endpoint = `${backendUrl}forecastmodels/get-alert/?variable=tp&admin_level=${adminLevel}`;
 
  try {
    const res = await fetch(endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
 
    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(
        `HTTP error! Status: ${res.status}, Message: ${errorText}`
      );
    }
 
    const data = await res.json();
    // { source, latest_init_time, data: { <adm_code>: [{ period_start, period_end, aggregate_value }] } }
    return data || {};
  } catch (error) {
    console.error(`Error fetching all ${type} forecast data:`, error);
    return {};
  }
};
 


// const processForecastDataForDays = (allForecasts, targetDates) => {
//   const processedData = {};
//   targetDates.forEach((date) => {
//     processedData[date] = {};
//   });

//   allForecasts.forEach((item) => {
//     const code = item.adm_code || item.sub_adm_code;
//     const itemForecastDate = item.fcst_date;

//     if (targetDates.includes(itemForecastDate)) {
//       processedData[itemForecastDate][code] = item.rainfall;
//     }
//   });
//   return processedData;
// };


const processForecastDataForDays = (forecastResponse, targetDates) => {
  const processedData = {};
  
  // Initialize structure for each target date
  targetDates.forEach((date) => {
    processedData[date] = {};
  });
 
  // Check if we have the expected data structure
  if (!forecastResponse || !forecastResponse.data) {
    console.warn("No forecast data available");
    return processedData;
  }
 
  const { data: admCodeData } = forecastResponse;
 
  // Process each admin code's forecast data
  Object.entries(admCodeData).forEach(([admCode, forecastArray]) => {
    if (!Array.isArray(forecastArray)) return;
 
    forecastArray.forEach((forecastItem) => {
      // Extract date from period_start (format: "2026-05-12T00:00:00")
      const periodStart = forecastItem.period_start;
      if (!periodStart) return;
 
      // Extract date portion (YYYY-MM-DD)
      const dateOnly = periodStart.split("T")[0];
 
      // Check if this date matches any of our target dates
      if (targetDates.includes(dateOnly)) {
        // Store the aggregate_value as rainfall for this admin code and date
        processedData[dateOnly][admCode] = forecastItem.aggregate_value;
      }
    });
  });
 
  return processedData;
};


const MapBoundsUpdater = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.flyToBounds(bounds);
    }
  }, [map, bounds]);

  return null;
};

const ZoomToFeature = ({ feature }) => {
  const map = useMap();
  useEffect(() => {
    if (feature) {
      const geojsonLayer = L.geoJSON(feature);
      map.flyToBounds(geojsonLayer.getBounds(), { padding: [20, 20] });
    }
  }, [feature, map]);
  return null;
};

const WmsLandslideLayer = ({ selectedDate }) => {
  const map = useMap();
  const wmsLayerRef = useRef(null);

  useEffect(() => {
    if (!map || !selectedDate) return;

    const formattedLayerDate = selectedDate;
    const layerName = `test_lsi:landslide_forecast_${formattedLayerDate}`;
    const wmsBaseUrl = `https://drr-dss.rimes.int/landslide_trigrs?service?`;

    if (wmsLayerRef.current && map.hasLayer(wmsLayerRef.current)) {
      map.removeLayer(wmsLayerRef.current);
      wmsLayerRef.current = null;
    }

    const newWmsLayer = L.tileLayer.wms(wmsBaseUrl, {
      layers: layerName,
      format: "image/png",
      styles: 'test_lsi:lsi_risk',
      transparent: true,
      version: "1.1.0",
      crs: L.CRS.EPSG3857,
      attribution: "Map data © ECMWF - Data updation daily at 9AM (UTC)",
    });

    newWmsLayer.addTo(map);
    wmsLayerRef.current = newWmsLayer;

    return () => {
      if (wmsLayerRef.current && map.hasLayer(wmsLayerRef.current)) {
        map.removeLayer(wmsLayerRef.current);
      }
    };
  }, [map, selectedDate]);

  return null;
};

const WmsHouseholdLayer = ({ selectedDate }) => {
  const map = useMap();
  const wmsLayerRef = useRef(null);

  useEffect(() => {
    if (!map || !selectedDate) return;
    const householdFormattedLayerDate = selectedDate.replace(/-/g, "");
    console.log("Selected Date for Household Layer:", householdFormattedLayerDate);
    const wmsBaseUrl = `https://drr-dss.rimes.int/landslide_trigrs?service?`;
    const layerName = `test_lsi:household_forecast_latest_${householdFormattedLayerDate}`;
    if (wmsLayerRef.current && map.hasLayer(wmsLayerRef.current)) {
      map.removeLayer(wmsLayerRef.current);
      wmsLayerRef.current = null;
    }

    const newWmsLayer = L.tileLayer.wms(wmsBaseUrl, {
      layers: layerName,
      styles: 'test_lsi:Household_LSI_Style',
      format: "image/png",
      transparent: true,
      version: "1.1.0",
      crs: L.CRS.EPSG3857,
      attribution: "Household data",
    });

    newWmsLayer.addTo(map);
    wmsLayerRef.current = newWmsLayer;

    return () => {
      if (wmsLayerRef.current) {
        map.removeLayer(wmsLayerRef.current);
      }
    };
  }, [map, selectedDate]);

  return null;
};

const hardcodedDates = [
  getForecastDate(0),
  getForecastDate(1),
  getForecastDate(2),
];

const Forecast = () => {
  const { hasPermission } = usePermissions();
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [municipalityData, setMunicipalityData] = useState(null);
  const [subMunicipalityData, setSubMunicipalityData] = useState(null);
  const [sucoData, setSucoData] = useState(null);
  const [activeSection, setActiveSection] = useState("area");
  const [tabularData, setTabularData] = useState([]);
  const [fullLandslideImpactData, setFullLandslideImpactData] = useState({});
  const [landslideTableDataAllDays, setLandslideTableDataAllDays] = useState([]); // RENAMED
  const [filteredTabularData, setFilteredTabularData] = useState([]);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const overviewRef = useRef(null);
  const mapRef = useRef(null);
  const rainfallForecastRef = useRef(null);
  const landslideAdvisoryRef = useRef(null);
  const impactedAreaRef = useRef(null);
  const [advisoryData, setAdvisoryData] = useState([]);
  const [mapBounds, setMapBounds] = useState(null);
  const [pdfLanguage, setPdfLanguage] = useState("en");
  // Risk levels listed in the report's key table (High + Medium by default).
  const [reportRiskLevels, setReportRiskLevels] = useState(["High", "Medium"]);
  const [isDataReady, setIsDataReady] = useState(false);

  const [municipalities, setMunicipalities] = useState([]);
  const [allSubMunicipalities, setAllSubMunicipalities] = useState([]);
  const [allSucos, setAllSucos] = useState([]);
  const [subMunicipalities, setSubMunicipalities] = useState([]);
  const [sucos, setSucos] = useState([]);
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [selectedSubMunicipality, setSelectedSubMunicipality] = useState("");
  const [selectedSuco, setSelectedSuco] = useState("");
  const [adm1Data, setAdm1Data] = useState(null);
  const [adm2Data, setAdm2Data] = useState(null);
  const [adm3Data, setAdm3Data] = useState(null);
  const [zoomFeature, setZoomFeature] = useState(null);

  const [initialMapBounds, setInitialMapBounds] = useState(null);
  const [resetTrigger] = useState(0);
  const [showHouseholdLayer, setShowHouseholdLayer] = useState(false);
  const [showForecastLayer, setShowForecastLayer] = useState(true);
  const [forecastPanelCollapsed, setForecastPanelCollapsed] = useState(true);

  // const loadImage = (src) => {
  //   return new Promise((resolve, reject) => {
  //     const img = new Image();
  //     img.onload = () => resolve(img);
  //     img.onerror = reject;
  //     img.src = src;
  //   });
  // };

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        const today = getForecastDate(0);
        const tomorrow = getForecastDate(1);
        const dayAfterTomorrow = getForecastDate(2);
        const forecastDates = [today, tomorrow, dayAfterTomorrow];

        const [
          municipalityGeo,
          subMunicipalityGeo,
          sucoGeo,
          allSucoForecastData,
          munisResp,
          landslideResp,
          subMunisResp,
          sucosResp,
        ] = await Promise.all([
          fetch("/DATA/OSMbased_adm1_boundary.json").then((res) => {
            if (!res.ok)
              throw new Error("Failed to fetch Municipality GeoJSON");
            return res.json();
          }),
          fetch("/DATA/OSMbased_adm2_boundary.json").then((res) => {
            if (!res.ok)
              throw new Error("Failed to fetch Sub-Municipality GeoJSON");
            return res.json();
          }),
          fetch("/DATA/OSMbased_adm3_boundary.json").then((res) => {
            if (!res.ok) throw new Error("Failed to fetch Suco GeoJSON");
            return res.json();
          }),
          fetchAllForecastMapData("suco"),
          axios.post(`${backendUrl}Municipalities/municipalities_list/`),
          fetchAndProcessFullLandslideData(),
          axios.post(`${backendUrl}Municipalities/sub_municipalities_list/`),
          axios.post(`${backendUrl}Municipalities/suco_list/`),
        ]);

        const processedForecasts = processForecastDataForDays(
          allSucoForecastData,
          forecastDates
        );

        const tabularDataFromSuco = sucoGeo?.features
        ? sucoGeo.features.map((feature) => {
            const admCode = feature.properties.ADM_Code;
            return {
              municipality: feature.properties.ADM1,
              subMunicipality: feature.properties.ADM2,
              Suco: feature.properties.ADM3,
              admCode,
              // ?? (not ||) so a real 0 mm stays 0 instead of becoming N/A
              rainfallDay1: processedForecasts[today]?.[admCode] ?? null,
              rainfallDay2: processedForecasts[tomorrow]?.[admCode] ?? null,
              rainfallDay3: processedForecasts[dayAfterTomorrow]?.[admCode] ?? null,
              Population: feature.properties.Population || 0,
              Male: feature.properties.Male || 0,
              Female: feature.properties.Female || 0,
              HH_Urban: feature.properties.HH_Urban || 0,
              HH_Rural: feature.properties.HH_Rural || 0,
              Household: feature.properties.Household || 0,
            };
          })
        : [];

        setAdm1Data(municipalityGeo);
        setAdm2Data(subMunicipalityGeo);
        setAdm3Data(sucoGeo);
        setMunicipalityData(municipalityGeo);
        setSubMunicipalityData(subMunicipalityGeo);
        setSucoData(sucoGeo);
        setTabularData(tabularDataFromSuco);
        setFullLandslideImpactData(landslideResp.fullData);
        setLandslideTableDataAllDays(landslideResp.tableData); // Used for all 3 days' data (PDF)
        setMunicipalities(munisResp.data);

        setAllSubMunicipalities(subMunisResp.data);
        setAllSucos(sucosResp.data);

        setSubMunicipalities(subMunisResp.data);
        setSucos(sucosResp.data);

        if (
          municipalityGeo &&
          municipalityGeo.features &&
          municipalityGeo.features.length > 0
        ) {
          const geoJsonLayer = L.geoJSON(municipalityGeo);
          const initialBounds = geoJsonLayer.getBounds();
          setMapBounds(initialBounds);
          setInitialMapBounds(initialBounds);
        }

        setIsDataReady(true);
      } catch (error) {
        console.error("Error loading initial data:", error);
        alert(`Failed to load initial data. ${error.message}`);
        setIsDataReady(false);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  // Filtered Rainfall and Landslide Data based on dropdowns
  const currentLandslideTableData = useMemo(() => {
    let landslideData = Object.keys(fullLandslideImpactData).map((admCode) => {
      const sucoDates = fullLandslideImpactData[admCode];
      const info = sucoDates.info || {};
      const dates = [getForecastDate(0), getForecastDate(1), getForecastDate(2)];
      return {
        Municipality: info.municipality || "N/A",
        PostAdmin: info.sub_municipality || "N/A",
        Suco: info.suco || "N/A",
        admCode: admCode,
        CharacteristicDay1: sucoDates[dates[0]]?.advisory || "N/A",
        CharacteristicDay2: sucoDates[dates[1]]?.advisory || "N/A",
        CharacteristicDay3: sucoDates[dates[2]]?.advisory || "N/A",
        Population: info.Population || 0,
        Male: info.Male || 0,
        Female: info.Female || 0,
        HH_Urban: info.HH_Urban || 0,
        HH_Rural: info.HH_Rural || 0,
        Household: info.Household || 0,
      };
    });
    if (selectedMunicipality) {
      const muniName = municipalities.find((m) => m.adm1_code === selectedMunicipality)?.adm1;
      landslideData = landslideData.filter((item) => item.Municipality === muniName);
    }
    if (selectedSubMunicipality) {
      const subMuniName = allSubMunicipalities.find((s) => s.adm2_code === selectedSubMunicipality)?.adm2;
      landslideData = landslideData.filter((item) => item.PostAdmin === subMuniName);
    }
    if (selectedSuco) {
      const sucoName = allSucos.find((s) => s.adm3_code === selectedSuco)?.adm3;
      landslideData = landslideData.filter((item) => item.Suco === sucoName);
    }
    return landslideData;
  }, [selectedMunicipality, selectedSubMunicipality, selectedSuco, fullLandslideImpactData, municipalities, allSubMunicipalities, allSucos]);

  const currentRainfallTableData = useMemo(() => {
    let rainfallData = tabularData;
    if (selectedMunicipality) {
      const muniName = municipalities.find(
        (m) => m.adm1_code === selectedMunicipality
      )?.adm1;
      rainfallData = rainfallData.filter(
        (item) => item.municipality === muniName
      );
    }
    if (selectedSubMunicipality) {
      const subMuniName = allSubMunicipalities.find(
        (s) => s.adm2_code === selectedSubMunicipality
      )?.adm2;
      rainfallData = rainfallData.filter(
        (item) => item.subMunicipality === subMuniName
      );
    }
    if (selectedSuco) {
      const sucoName = allSucos.find((s) => s.adm3_code === selectedSuco)?.adm3;
      rainfallData = rainfallData.filter((item) => item.Suco === sucoName);
    }
    return rainfallData;
  }, [
    selectedMunicipality,
    selectedSubMunicipality,
    selectedSuco,
    tabularData,
    municipalities,
    allSubMunicipalities,
    allSucos,
  ]);

  // Update the state for children components
  useEffect(() => {
    setFilteredTabularData(currentRainfallTableData);
    // Renamed state to hold the 3-day data filtered by dropdowns
    setLandslideTableDataAllDays(currentLandslideTableData);
  }, [currentRainfallTableData, currentLandslideTableData]);


  useEffect(() => {
    const masterSucos = allSucos;
    if (selectedMunicipality) {
      const filteredSubMunis = allSubMunicipalities.filter(
        (s) => s.adm1_code === selectedMunicipality
      );
      setSubMunicipalities(filteredSubMunis);
      const filteredSucos = masterSucos.filter(
        (s) => s.adm1_code === selectedMunicipality
      );
      setSucos(filteredSucos);
    } else {
      setSubMunicipalities(allSubMunicipalities);
      setSucos(allSucos);
    }
    setSelectedSubMunicipality("");
    setSelectedSuco("");
  }, [selectedMunicipality, allSubMunicipalities, allSucos]);

  useEffect(() => {
    const masterSucos = allSucos;
    if (selectedSubMunicipality) {
      const filteredSucos = masterSucos.filter(
        (s) => s.adm2_code === selectedSubMunicipality
      );
      setSucos(filteredSucos);
    } else if (selectedMunicipality) {
      const filteredSucos = masterSucos.filter(
        (s) => s.adm1_code === selectedMunicipality
      );
      setSucos(filteredSucos);
    } else {
      setSucos(masterSucos);
    }
    setSelectedSuco("");
  }, [selectedSubMunicipality, selectedMunicipality, allSucos]);

  useEffect(() => {
    let featureToZoom = null;
    if (selectedSuco && adm3Data) {
      featureToZoom = adm3Data.features.find(
        (f) => f.properties.ADM_Code === selectedSuco
      );
    } else if (selectedSubMunicipality && adm2Data) {
      featureToZoom = adm2Data.features.find(
        (f) => f.properties.ADM_Code === selectedSubMunicipality
      );
    } else if (selectedMunicipality && adm1Data) {
      featureToZoom = adm1Data.features.find(
        (f) => f.properties.ADM_Code === selectedMunicipality
      );
    }
    setZoomFeature(featureToZoom);
  }, [
    selectedMunicipality,
    selectedSubMunicipality,
    selectedSuco,
    adm1Data,
    adm2Data,
    adm3Data,
  ]);

  useEffect(() => {
    if (resetTrigger > 0) {
      setSelectedMunicipality("");
      setSelectedSubMunicipality("");
      setSelectedSuco("");
      setZoomFeature(null);
      if (initialMapBounds) {
        setMapBounds(null);
        setTimeout(() => setMapBounds(initialMapBounds), 0);
      }
    }
  }, [resetTrigger, initialMapBounds]);

  useEffect(() => {
    const fetchAdvisoryData = async () => {
      try {
        const response = await axios.get(
          `${backendUrl}landslide/landslide-advisories/`
        );
        setAdvisoryData(response.data);
      } catch (error) {
        console.error("Error fetching landslide advisories:", error);
      }
    };
    fetchAdvisoryData();
  }, []);

  useEffect(() => {
    setSelectedDate(hardcodedDates[selectedDateIndex]);
  }, [selectedDateIndex]);

  const handlePrevDate = () => {
    if (selectedDateIndex > 0) {
      setSelectedDateIndex((prev) => prev - 1);
    }
  };

  const handleNextDate = () => {
    if (selectedDateIndex < hardcodedDates.length - 1) {
      setSelectedDateIndex((prev) => prev + 1);
    }
  };

  const getBoundaryStyle = () => ({
    color: "black",
    weight: 1,
    fillOpacity: 0,
  });

  const selectedGeojsonStyle = (feature) => {
    if (feature.properties.ADM_Code) {
      if (feature.properties.ADM_Code === selectedSuco) {
        return {
          color: "white",
          weight: 3,
          fillOpacity: 0,
        };
      }
    }
    if (feature.properties.ADM2_Code) {
      if (feature.properties.ADM2_Code === selectedSubMunicipality) {
        return {
          color: "white",
          weight: 3,
          fillOpacity: 0,
        };
      }
    }
    if (feature.properties.ADM1_Code) {
      if (feature.properties.ADM1_Code === selectedMunicipality) {
        return {
          color: "white",
          weight: 3,
          fillOpacity: 0,
        };
      }
    }
    return getBoundaryStyle();
  };

  const handleGenerateReportClick = () => {
    if (!isDataReady) {
      alert(
        "Please wait until all data is loaded and report generation is possible."
      );
      return;
    }
    setShowLanguageModal(true);
  };

  const toggleReportRiskLevel = (level) => {
    setReportRiskLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
    );
  };

  const handleLanguageSelectAndGenerate = async (selectedLanguage) => {
    if (reportRiskLevels.length === 0) {
      alert("Please select at least one risk level for the report.");
      return;
    }
    setPdfLanguage(selectedLanguage);
    setShowLanguageModal(false);
    setLoading(true);
    try {
      const highlightFeature =
        (selectedSuco && adm3Data?.features.find((f) => f.properties.ADM_Code === selectedSuco)) ||
        (selectedSubMunicipality && adm2Data?.features.find((f) => f.properties.ADM_Code === selectedSubMunicipality)) ||
        (selectedMunicipality && adm1Data?.features.find((f) => f.properties.ADM_Code === selectedMunicipality)) ||
        null;
      await generateLandslideAdvisory({
        language: selectedLanguage,
        selectedDate,
        selectedDateIndex,
        rows: landslideTableDataAllDays,
        rainfallRows: filteredTabularData,
        riskLevels: reportRiskLevels,
        levelText: selectedSuco ? "Suco Level"
          : selectedSubMunicipality ? "Post Admin Level"
          : selectedMunicipality ? "Municipality Level"
          : "Country Level",
        showForecastLayer,
        showHouseholdLayer,
        sucoData,
        municipalityData,
        highlightFeature,
      });
    } catch (fatalErr) {
      console.error("Fatal PDF error:", fatalErr);
      alert(`PDF generation failed: ${fatalErr.message || "Unknown error"}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const colorMap = useMemo(() => {
    return {
      NoRisk: characteristicColors["NoRisk"],
      Low: characteristicColors["Low"],
      Medium: characteristicColors["Medium"],
      High: characteristicColors["High"],
      "N/A": characteristicColors["N/A"] || [222, 222, 223],
    };
  }, []);

  const landslideDataByAdmCodeMap = useMemo(() => {
    return new Map(Object.entries(fullLandslideImpactData));
  }, [fullLandslideImpactData]);

  const onEachSucoFeature = useCallback(
    (feature, layer) => {
      const admCode = feature.properties.ADM_Code;
      const sucoDataForDates = landslideDataByAdmCodeMap.get(admCode);
      const sucoName = feature.properties.ADM3;

      if (sucoDataForDates) {
        const dataForSelectedDate = sucoDataForDates[selectedDate];

        let advisoryText = "N/A";
        let riskLevel = "N/A";
        let municipality =
          sucoDataForDates.info?.municipality ||
          feature.properties.ADM1 ||
          "N/A";
        let sub_municipality =
          sucoDataForDates.info?.sub_municipality ||
          feature.properties.ADM2 ||
          "N/A";
        let date = selectedDate;

        if (dataForSelectedDate) {
          advisoryText = dataForSelectedDate.advisory || "N/A";
          riskLevel = getLandslideCharacteristic(advisoryText);
          municipality = dataForSelectedDate.municipality || municipality;
          sub_municipality =
            dataForSelectedDate.sub_municipality || sub_municipality;
          date = dataForSelectedDate.date || date;
        }

        const colorRgbArray = colorMap[riskLevel];
        const color = `rgb(${colorRgbArray.join(",")})`;

        let popupContent = `
        <div style="font-family: Arial, sans-serif; font-size: 13px; max-width:270px;">
          <h3 style="margin-bottom:4px;">Suco: <b>${sucoName}</b></h3>
          <div style="font-size:12px;">
            <b>Municipality:</b> ${municipality}<br/>
            <b>Post Admin:</b> ${sub_municipality}<br/>
            <b>Date:</b> ${date}<br/>
            <b>Total Population:</b> ${sucoDataForDates.info?.Population || feature.properties.Population || 0}<br/>
            <b>Total Male:</b> ${sucoDataForDates.info?.Male || feature.properties.Male || 0}<br/>
            <b>Total Female:</b> ${sucoDataForDates.info?.Female || feature.properties.Female || 0}<br/>
            <b>Total children (under 17 years old):</b> ${sucoDataForDates.info?.HH_Urban || feature.properties.HH_Urban || 0}<br/>
            <b>Total elderly:</b> ${sucoDataForDates.info?.HH_Rural || feature.properties.HH_Rural || 0}<br/>
            <b>Household:</b> ${sucoDataForDates.info?.Household || feature.properties.Household || 0}<br/>
                        <b>Advisory:</b> 
            <span style="
              background-color: ${color}; 
              padding: 2px 5px; 
              border-radius: 3px; 
              font-weight: bold; 
              color: black; 
              display: inline-block;
              text-align: center;
              min-width: 50px;
            ">
              ${advisoryText}
            </span><br/>
          </div>
        </div>
      `;
        layer.bindPopup(popupContent);
      } else {
        layer.bindPopup(`No data available for ${sucoName}`);
      }
    },
    [landslideDataByAdmCodeMap, selectedDate, colorMap]
  );

  return (
    <>
      <div className="map-container-landslide">
        {/* Generate Report Button - above filter bar */}
        {hasPermission('landslide_forecast_generate_report') && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
          <button
            onClick={handleGenerateReportClick}
            disabled={loading || !isDataReady}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 16px",
              backgroundColor: loading || !isDataReady ? "#90caf9" : "#0d6efd",
              color: "#ffffff",
              border: "none",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: 600,
              cursor: loading || !isDataReady ? "not-allowed" : "pointer",
              boxShadow: "0 2px 6px rgba(13, 110, 253, 0.3)",
              transition: "all 0.2s ease",
              whiteSpace: "nowrap",
            }}
          >
            {loading ? (
              <CircularProgress size={18} sx={{ color: "#ffffff" }} />
            ) : (
              <DescriptionIcon sx={{ fontSize: 18 }} />
            )}
            Generate Report
          </button>
        </Box>
        )}

        {/* Filter Bar - above map */}
        <Box
          sx={{
            p: 2.5,
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.03)",
            border: "1px solid #e8ecf1",
            marginBottom: "16px",
            overflow: "visible",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* Filter Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <FilterAltIcon sx={{ color: "#0d6efd", fontSize: 22 }} />
              <span style={{ fontWeight: 700, fontSize: "15px", color: "#333" }}>Filter</span>
            </Box>

            {/* Divider */}
            <Box sx={{ width: "1px", height: "36px", backgroundColor: "#e0e0e0" }} />

            {/* Date Section */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span style={{ fontWeight: 600, fontSize: "14px", color: "#333", whiteSpace: "nowrap" }}>Date:</span>
              <button
                onClick={handlePrevDate}
                className="date-nav-button"
                disabled={selectedDateIndex === 0}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px", color: "#0d6efd" }}
              >
                <FontAwesomeIcon icon={faArrowCircleLeft} />
              </button>
              <div style={{ fontSize: "13px", fontWeight: 500, color: "#555", whiteSpace: "nowrap" }}>
                {formatDateForDisplay(selectedDate, selectedDateIndex)}
              </div>
              <button
                onClick={handleNextDate}
                disabled={selectedDateIndex === hardcodedDates.length - 1}
                className="date-nav-button"
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px", color: "#0d6efd" }}
              >
                <FontAwesomeIcon icon={faArrowCircleRight} />
              </button>
            </Box>

            {/* Divider */}
            <Box sx={{ width: "1px", height: "36px", backgroundColor: "#e0e0e0" }} />

            {/* Location Filters — not required, commented out
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 600, fontSize: "14px", color: "#333", whiteSpace: "nowrap" }}>Location:</span>
              <Form.Select
                name="municipality"
                value={selectedMunicipality}
                onChange={handleMunicipalityChange}
                style={{ width: "180px", fontSize: "13px", padding: "6px 10px" }}
              >
                <option value="">Municipality</option>
                {municipalities.map((municipality, index) => (
                  <option key={index} value={municipality.adm1_code}>
                    {municipality.adm1}
                  </option>
                ))}
              </Form.Select>

              <Form.Select
                name="sub_municipality"
                value={selectedSubMunicipality}
                onChange={handleSubMunicipalityChange}
                disabled={!selectedMunicipality}
                style={{ width: "180px", fontSize: "13px", padding: "6px 10px" }}
              >
                <option value="">
                  {selectedMunicipality ? "Post-Admin" : "Select municipality first"}
                </option>
                {subMunicipalities.map((submunicipality, index) => (
                  <option key={index} value={submunicipality.adm2_code}>
                    {submunicipality.adm2}
                  </option>
                ))}
              </Form.Select>

              <Form.Select
                name="suco"
                value={selectedSuco}
                onChange={handleSucoChange}
                disabled={!selectedSubMunicipality}
                style={{ width: "180px", fontSize: "13px", padding: "6px 10px" }}
              >
                <option value="">
                  {selectedSubMunicipality ? "Suco" : "Select Post-Admin first"}
                </option>
                {sucos.map((suco, index) => (
                  <option key={index} value={suco.adm3_code}>
                    {suco.adm3}
                  </option>
                ))}
              </Form.Select>

              <IconButton
                onClick={() => setResetTrigger((prev) => prev + 1)}
                sx={{
                  backgroundColor: "#f5f5f5",
                  padding: "6px",
                  borderRadius: "50%",
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.08)" },
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
                title="Reset filters"
              >
                <RestartAltIcon sx={{ color: "#0d6efd", fontSize: 20 }} />
              </IconButton>
            </Box>
            */}
          </Box>
        </Box>

        <div className="map-wrapper" ref={mapRef}>
          <MapContainer
            center={[INITIAL_VIEW.lat, INITIAL_VIEW.lng]}
            zoom={INITIAL_VIEW.zoom}
            className="mapcontainerall"
          >
            <MapHomeControl />
            <MapBoundsUpdater bounds={mapBounds} />
            {zoomFeature && (
              <ZoomToFeature
                feature={{ type: "FeatureCollection", features: [zoomFeature] }}
              />
            )}
            <Information />

            {selectedDate && showForecastLayer && (
              <WmsLandslideLayer selectedDate={selectedDate} />
            )}

            {showHouseholdLayer && selectedDate && (
              <WmsHouseholdLayer selectedDate={selectedDate} />
            )}

            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

            {municipalityData && (
              <GeoJSON
                data={municipalityData}
                style={selectedGeojsonStyle}
              />
            )}
            {subMunicipalityData && (
              <GeoJSON
                data={subMunicipalityData}
                style={selectedGeojsonStyle}
              />
            )}
            {sucoData && (
              <GeoJSON
                key={selectedDate}
                data={sucoData}
                style={selectedGeojsonStyle}
                onEachFeature={onEachSucoFeature}
              />
            )}

            {/* Custom Layer Toggle Control */}
            <div
              style={{
                position: "absolute",
                right: 12,
                top: 10,
                zIndex: 1000,
                background: "rgba(255,255,255,0.95)",
                backdropFilter: "blur(10px)",
                borderRadius: "12px",
                boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                padding: "10px 12px",
                border: "1px solid #e8ecf1",
                minWidth: "200px",
              }}
            >
              <div
                onClick={() => setForecastPanelCollapsed((prev) => !prev)}
                style={{
                  fontWeight: 700,
                  fontSize: "14px",
                  color: "#333",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  userSelect: "none",
                  marginBottom: forecastPanelCollapsed ? 0 : "10px",
                }}
              >
                <span>Forecast Layers</span>
                <span style={{ fontSize: "11px", color: "#666", marginLeft: "8px" }}>
                  {forecastPanelCollapsed ? "▼" : "▲"}
                </span>
              </div>
              {!forecastPanelCollapsed && (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px" }}>
                    <input
                      type="checkbox"
                      checked={showForecastLayer}
                      onChange={(e) => setShowForecastLayer(e.target.checked)}
                      style={{ cursor: "pointer", width: "16px", height: "16px" }}
                    />
                    <span>Landslide Forecast</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px" }}>
                    <input
                      type="checkbox"
                      checked={showHouseholdLayer}
                      onChange={(e) => setShowHouseholdLayer(e.target.checked)}
                      style={{ cursor: "pointer", width: "16px", height: "16px" }}
                    />
                    <span>Household Forecast</span>
                  </label>
                </div>
              )}
            </div>

            <MapLegendControl
              position="bottomright"
              sections={[
                {
                  shape: "square",
                  items: [
                    { label: "High", color: "rgb(230, 45, 58)" },
                    { label: "Medium", color: "rgb(243, 155, 58)" },
                    { label: "Low", color: "rgb(243, 235, 18)" },
                    { label: "No-Risk", color: "rgb(148, 203, 85)" },
                    { label: "N/A", color: "rgb(222,222,223)" },
                  ],
                },
              ]}
            />
          </MapContainer>
        </div>

        <>
          <div className="section-buttons-container">
            <button
              className={`section-button ${
                activeSection === "area" ? "active" : ""
              }`}
              onClick={() => setActiveSection("area")}
            >
              Impacted Area
            </button>
            <button
              className={`section-button ${
                activeSection === "rainfall" ? "active" : ""
              }`}
              onClick={() => setActiveSection("rainfall")}
            >
              Rainfall Forecast
            </button>
          </div>

          <div className="section-content-container">
            <div
              ref={overviewRef}
              className={`section-container ${
                activeSection === "overview"
                  ? "active-section"
                  : "hidden-section"
              }`}
              dangerouslySetInnerHTML={{
                __html: translations[pdfLanguage].overviewText,
              }}
            />

            {activeSection === "area" && (
              <ImpactedAreaLS
                loading={loading}
                selectedMunicipality={selectedMunicipality}
                selectedSubMunicipality={selectedSubMunicipality}
                selectedSuco={selectedSuco}
                municipalities={municipalities}
                subMunicipalities={subMunicipalities}
                sucos={sucos}
                tableData={landslideTableDataAllDays}
                selectedDateIndex={selectedDateIndex}
                ref={impactedAreaRef}
              />
            )}

            {activeSection === "rainfall" && (
                <RainfallForecast
                    ref={rainfallForecastRef}
                    tabularData={filteredTabularData}
                    loading={loading}
                    selectedDateIndex={selectedDateIndex} 
                />
            )}

            <div
              ref={landslideAdvisoryRef}
              className={`section-container ${
                activeSection === "advisory"
                  ? "active-section"
                  : "hidden-section"
              }`}
            >
              <LandslideAdvisory advisoryData={advisoryData} />
            </div>
          </div>
        </>
        <Modal
          show={showLanguageModal}
          onHide={() => setShowLanguageModal(false)}
          centered
          backdrop="static"
        >
          <Modal.Body style={{ padding: 0, border: "none", background: "transparent" }}>
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "16px",
                padding: "32px 36px",
                textAlign: "center",
              }}
            >
              {/* Icon */}
              <div style={{
                width: "52px",
                height: "52px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #e8f0fe, #d4e4fd)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                fontSize: "22px",
              }}>
                🌐
              </div>

              <h3 style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: 700, color: "#1a1a2e" }}>
                Report Options
              </h3>
              <p style={{ margin: "0 0 14px", fontSize: "13px", color: "#6b7280", lineHeight: 1.5 }}>
                Choose what the advisory PDF lists, then pick a language to generate it.
              </p>
              <div style={{ textAlign: "left", margin: "0 0 18px" }}>
                <div style={{ fontWeight: 600, marginBottom: "8px", color: "#333", fontSize: "13px" }}>
                  Risk levels to include in the report table
                </div>
                {[
                  { key: "High", label: "High", color: "rgb(230, 45, 58)" },
                  { key: "Medium", label: "Medium", color: "rgb(243, 155, 58)" },
                  { key: "Low", label: "Low", color: "rgb(243, 235, 18)" },
                  { key: "NoRisk", label: "No Risk", color: "rgb(148, 203, 85)" },
                ].map(({ key, label, color }) => (
                  <label
                    key={key}
                    style={{ display: "flex", alignItems: "center", gap: "8px", margin: "4px 0", cursor: "pointer", fontSize: "13px" }}
                  >
                    <input
                      type="checkbox"
                      checked={reportRiskLevels.includes(key)}
                      onChange={() => toggleReportRiskLevel(key)}
                    />
                    <span style={{ width: 14, height: 14, borderRadius: 3, background: color, display: "inline-block" }} />
                    {label}
                  </label>
                ))}
              </div>
              <div style={{ fontWeight: 600, marginBottom: "10px", color: "#333", fontSize: "13px", textAlign: "left" }}>
                Select report language
              </div>

              <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                {/* English Card */}
                <div
                  onClick={() => handleLanguageSelectAndGenerate("en")}
                  style={{
                    flex: 1,
                    maxWidth: "160px",
                    padding: "18px 12px",
                    borderRadius: "12px",
                    border: "1.5px solid #e2e8f0",
                    backgroundColor: "#fafbfc",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#0d6efd";
                    e.currentTarget.style.backgroundColor = "#f0f6ff";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(13, 110, 253, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.backgroundColor = "#fafbfc";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <span style={{ fontSize: "28px" }}>🇬🇧</span>
                  <span style={{ fontSize: "14px", fontWeight: 600, color: "#1a1a2e" }}>English</span>
                </div>

                {/* Tetum Card */}
                <div
                  onClick={() => handleLanguageSelectAndGenerate("tetum")}
                  style={{
                    flex: 1,
                    maxWidth: "160px",
                    padding: "18px 12px",
                    borderRadius: "12px",
                    border: "1.5px solid #e2e8f0",
                    backgroundColor: "#fafbfc",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#28a745";
                    e.currentTarget.style.backgroundColor = "#f0faf3";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(40, 167, 69, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.backgroundColor = "#fafbfc";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <span style={{ fontSize: "28px" }}>🇹🇱</span>
                  <span style={{ fontSize: "14px", fontWeight: 600, color: "#1a1a2e" }}>Tetum</span>
                </div>
              </div>

              {/* Cancel */}
              <button
                onClick={() => setShowLanguageModal(false)}
                style={{
                  marginTop: "20px",
                  padding: "8px 24px",
                  backgroundColor: "transparent",
                  color: "#6b7280",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: 500,
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f3f4f6";
                  e.currentTarget.style.borderColor = "#d1d5db";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.borderColor = "#e2e8f0";
                }}
              >
                Cancel
              </button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default Forecast;